/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement, useContext, useMemo, useRef } from 'react';
import LineChart from '../LineChart';
import { ThemeContext } from '../../context/theme.context';

export type LivePoint = { x: number; y: number };
export type LiveSeries = { label: string; color: string; points: LivePoint[]; unit?: string };

type Props = {
  series: LiveSeries[];
  /** Visible window in live mode: [now - windowMs, now]. */
  windowMs: number;
  /** Fixed visible range (history mode); overrides the rolling window. */
  range?: { from: number; to: number };
  /**
   * Points further apart than this are NOT joined, so a real outage shows as
   * a break. Must exceed the spacing of the data itself — history points are
   * bucket-averages minutes apart, and a too-small value draws nothing.
   */
  gapMs?: number;
  /** Bumped by the page whenever new data has been appended or time moved on. */
  version: number;
  unit?: string;
  height?: string;
  /**
   * 'shared' puts every series on one y axis — right when they are the same
   * quantity (accelerometer axes, one parameter).
   *
   * 'perSeries' gives each series its own axis, which is what makes a single
   * merged chart of every sensor parameter readable: CO₂ in the hundreds and
   * PM2.5 in single digits cannot share a scale without flattening one of them
   * into the baseline. Only the first axis is drawn, and hovering a series
   * brings its own axis forward.
   */
  axisMode?: 'shared' | 'perSeries';
  /**
   * Enables drag-to-zoom and pan on the time axis, reporting the range the
   * user settled on. Left off in live mode, where the axis is pinned to a
   * rolling window and would snap back on the next tick.
   */
  onRangeChange?: (from: number, to: number) => void;
};

/**
 * Live adapter over the dashboard's shared {@link LineChart}, so these graphs
 * look and behave (crosshair, click-to-pin tooltips) like every other line
 * chart in the app. The react-chartjs-2 wrapper updates the chart in place when
 * data changes; animation is off so each update is a cheap redraw. The axis
 * is a linear time scale rather than "timeseries", because live points are
 * irregularly spaced and must sit at their real times.
 */
/** Axis id for a series index — matches the dashboard's own `y`, `y1`, `y2` scheme. */
const axisIdFor = (index: number, mode: 'shared' | 'perSeries'): string =>
  mode === 'shared' || index === 0 ? 'y' : `y${index}`;

export default function LiveLineChart({
  series,
  windowMs,
  range,
  gapMs = 60000,
  version,
  unit,
  height = 'h-56',
  axisMode = 'shared',
  onRangeChange
}: Props): ReactElement {
  const { isDarkTheme } = useContext(ThemeContext);
  const tickColor = isDarkTheme ? '#98A2B3' : '#667085';
  const gridColor = isDarkTheme ? 'rgba(152,162,179,0.12)' : '#F2F4F7';
  // Held in a ref so changing the handler never rebuilds the chart options,
  // which would reset an in-progress zoom.
  const rangeChange = useRef(onRangeChange);
  rangeChange.current = onRangeChange;

  const data = useMemo(
    () => ({
      datasets: series.map((s, i) => ({
        label: s.label,
        data: s.points,
        borderColor: s.color,
        backgroundColor: s.color,
        borderWidth: 1.75,
        pointRadius: 0,
        pointHitRadius: 6,
        tension: 0,
        yAxisID: axisIdFor(i, axisMode),
        unit: s.unit ?? unit
      }))
    }),
    // `version` signals that the page's ref-held points changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, version, axisMode, unit]
  );

  const options = useMemo(() => {
    const now = Date.now();
    const zoomable = Boolean(onRangeChange);
    /**
     * Hands the settled axis limits back to the page, which owns the range for
     * every graph.
     *
     * It does NOT call `chart.resetZoom()` afterwards, though dropping the
     * plugin's own zoom state looks like the tidy thing to do: resetZoom fires
     * onZoomComplete, which lands straight back here, and the first drag on a
     * real chart blew the stack. Nor is it needed — react-chartjs-2 assigns a
     * fresh options object on every render, so the `min`/`max` this component
     * computes replace whatever the plugin wrote, and a preset chosen later
     * still wins.
     */
    const report = ({ chart }: any): void => {
      const scale = chart?.scales?.x;
      if (!scale || !rangeChange.current) return;
      const from = Math.round(scale.min);
      const to = Math.round(scale.max);
      if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
        rangeChange.current(from, to);
      }
    };

    // One axis per series when merged. Only the FIRST is given visible ticks:
    // eight stacked axes would leave no room for the data, and the tooltip
    // already carries each series' own value and unit.
    const scales: Record<string, any> = {
      x: {
        type: 'time',
        // Live: pinned to the rolling window so the axis scrolls even with
        // no data. History: the chosen range.
        min: range ? range.from : now - windowMs,
        max: range ? range.to : now,
        time: {
          tooltipFormat: range ? 'D MMM HH:mm:ss' : 'HH:mm:ss',
          displayFormats: {
            second: 'HH:mm:ss',
            minute: 'HH:mm',
            hour: 'D MMM HH:mm',
            day: 'D MMM'
          }
        },
        grid: { display: false },
        ticks: { color: tickColor, maxTicksLimit: 6, maxRotation: 0 }
      }
    };
    series.forEach((s, i) => {
      const id = axisIdFor(i, axisMode);
      if (scales[id]) return;
      const primary = id === 'y';
      scales[id] = {
        beginAtZero: false,
        position: 'left',
        display: primary,
        grid: { display: primary, color: gridColor },
        // On a merged chart the one visible axis belongs to a specific series,
        // so it is drawn in that series' colour. Without that the numbers read
        // as if they applied to every line on the chart.
        ticks: {
          color: axisMode === 'perSeries' ? s.color : tickColor,
          maxTicksLimit: 5
        }
      };
    });
    if (!scales.y) {
      scales.y = {
        beginAtZero: false,
        grid: { display: true, color: gridColor },
        ticks: { color: tickColor, maxTicksLimit: 5 }
      };
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      spanGaps: gapMs,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      plugins: {
        legend: {
          display: series.length > 1,
          position: 'bottom',
          labels: { color: tickColor, boxWidth: 10, boxHeight: 2, usePointStyle: false }
        },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              // Each series may carry its own unit on a merged chart, so the
              // tooltip takes the unit from the dataset and only falls back to
              // the chart-wide one.
              const own = ctx.dataset?.unit ?? unit;
              const shown = Number(ctx.parsed.y).toFixed(3).replace(/\.?0+$/, '');
              return `${ctx.dataset.label}: ${shown}${own ? ` ${own}` : ''}`;
            }
          }
        },
        // Drag or wheel to narrow the time axis; the page turns the result into
        // its shared range so every other graph follows.
        zoom: zoomable
          ? {
              limits: { x: { minRange: 10000 } },
              pan: { enabled: true, mode: 'x', modifierKey: 'shift', onPanComplete: report },
              zoom: {
                drag: { enabled: true, backgroundColor: 'rgba(46,144,250,0.15)' },
                wheel: { enabled: true, speed: 0.08 },
                pinch: { enabled: true },
                mode: 'x',
                onZoomComplete: report
              }
            }
          : { zoom: { drag: { enabled: false }, wheel: { enabled: false } } }
      },
      scales
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    windowMs,
    range?.from,
    range?.to,
    gapMs,
    version,
    unit,
    series,
    axisMode,
    tickColor,
    gridColor,
    onRangeChange
  ]);

  /**
   * Pins survive new pings arriving, and only clear when the chart starts
   * describing something else — a different device, a different set of series,
   * or a switch between live and a fixed range. Without this the default reset
   * key (the datasets array, rebuilt on every tick) threw a pin away almost as
   * soon as it was made.
   */
  const pinResetKey = useMemo(
    () => `${axisMode}|${range ? `${range.from}-${range.to}` : 'live'}|${series
      .map((s) => s.label)
      .join(',')}`,
    [axisMode, range?.from, range?.to, series]
  );

  return (
    <div className={`relative ${height} w-full`}>
      <LineChart data={data} options={options} pinResetKey={pinResetKey} />
    </div>
  );
}
